import { observable, action } from "mobx";
import makeApi, { URLS } from "Utils/apiURL";
import Utils from "Utils/utils";
import { importProgressJobs } from "Pages/Dashboard/sequences/ProgressJobs/ImportProgressJobsStore";

let accountNetworkRequest = {
  accounts: null,
  accountsFilter: null,
  accountContacts: null,
  accountContactsFilter: null,
  accountTasks: null,
  accountTasksFilter: null
};

const setAccountsNetworkRequest = (id = "") => {
  if (id && accountNetworkRequest?.[id] !== null) {
    accountNetworkRequest[id]("cancel");
    accountNetworkRequest[id] = null;
  }
};

const cancelAccountsReqCallback = (id = "", value = "") => {
  id && (accountNetworkRequest[id] = value);
};

export const initialContactsState = {
  searchTerm: "",
  limit: 10,
  activeTab: "TOTAL",
  stageId: null,
  pageNo: 1
};

class AccountsState {
  @observable pageNo = 1;
  @observable limit = 10;
  @observable totalPage = 1;
  @observable activeId = "";
  @observable accountName = "";
  @observable accountStages = [];
  @observable totalAccounts = "";
  @observable contactsStages = [];
  @observable searchTerm = "";
  // Named account lists + the active filter ("" = all). Lives in the store so
  // the header dropdown (Main.js) and the table (AccountsList.js) share it.
  @observable accountLists = [];
  @observable activeAccountListId = "";
  @observable contactsState = { ...initialContactsState };
  @observable reloadTasksPage = false;
  @observable reloadTasksTable = false;
  // Retain the applied filter across page navigation (survives leaving Accounts
  // and coming back within the session).
  @observable savedFilterData = null;
  @observable savedFilterList = [];

  @action
  setSavedFilterData = (val = null) => {
    this.savedFilterData = val;
  };

  @action
  setSavedFilterList = (val = []) => {
    this.savedFilterList = val;
  };

  @action
  setAccountLists = lists => {
    this.accountLists = lists || [];
  };

  @action
  setActiveAccountListId = id => {
    this.activeAccountListId = id || "";
    this.pageNo = 1;
  };

  @action
  fetchAccountLists = async () => {
    const response = await makeApi({ url: URLS.accountLists, method: "GET" });
    this.setAccountLists(response?.data?.data || []);
    return response?.data?.data || [];
  };

  @action
  createAccountList = async (name = "") => {
    const config = {
      url: URLS.accountLists,
      method: "POST",
      data: { name }
    };
    return await makeApi(config);
  };

  @action
  renameAccountList = async (listId = "", name = "") => {
    const config = {
      url: `${URLS.accountLists}/${listId}`,
      method: "PATCH",
      data: { name }
    };
    return await makeApi(config);
  };

  @action
  deleteAccountList = async (listId = "") => {
    const config = {
      url: `${URLS.accountLists}/${listId}`,
      method: "DELETE"
    };
    return await makeApi(config);
  };

  @action
  setSearchTerm = (val = "") => {
    this.searchTerm = val;
  };

  @action
  setTotalAccounts = (count = "") => {
    this.totalAccounts = count;
  };

  @action
  setAccountStages = (list = []) => {
    this.accountStages = list;
  };

  @action
  setAccountName = (name = "") => {
    this.accountName = name;
  };

  @action
  setPageNo = (no = "") => {
    this.pageNo = no;
  };

  @action
  setLimit = (val = "") => {
    this.limit = val;
  };

  @action
  setTotalPage = (page = 1) => {
    this.totalPage = page;
  };

  @action
  setActiveId = (activeId = "") => {
    this.activeId = activeId;
  };

  @action
  resetAccountListing = () => {
    this.activeId = "";
    this.totalPage = 1;
    this.pageNo = "";
    this.accountName = "";
    this.totalAccounts = "";
  };

  @action
  setContactsStages = (list = []) => {
    this.contactsStages = list;
  };

  @action
  getBulkAccountJobImportTracking = async () => {
    try {
      const response = await makeApi({
        url: URLS.accountBulkJobImportTracking,
        method: "GET"
      });
      const { status = "", data = [] } = response || {};
      if (+status === 200 && Array.isArray(data)) {
        importProgressJobs.setAccountCsvImportJobs(data);
        importProgressJobs.setTotalAccountCsvActiveJobs(
          Utils.countJobsByStatus(data, "TO_BE_PROCESSED")
        );
        importProgressJobs.setTotalAccountCsvInProgressJobs(
          Utils.countJobsByStatus(data, "IN_PROGRESS")
        );
      }
    } catch (e) {
      console.error("Error fetching the account bulk import jobs", e);
    }
  };

  @action
  setContactsState = (obj = {}) => {
    this.contactsState = { ...this.contactsState, ...obj };
  };

  @action
  setResetContactsState = () => {
    this.contactsState = { ...initialContactsState };
  };

  @action
  resetAccountListing = () => {
    this.activeId = "";
    this.totalPage = 1;
    this.pageNo = "";
    this.accountName = "";
    this.totalAccounts = "";
    this.searchTerm = "";
    this.limit = 10;
  };

  @action
  setReloadTasksPage = (val = false) => {
    this.reloadTasksPage = val;
  };

  @action
  setReloadTasksTable = (val = false) => {
    this.reloadTasksTable = val;
  };

  // Accounts
  @action
  enrichAccount = async (accountId = "") => {
    const config = {
      url: `${URLS.enrichAccount}/${accountId}/enrich`,
      method: "POST"
    };
    return await makeApi(config);
  };

  @action
  getAccountSubsidiaries = async (accountId = "") => {
    const config = {
      url: `${URLS.getAccountSubsidiaries}/${accountId}/subsidiaries`,
      method: "GET"
    };
    return await makeApi(config);
  };

  @action
  addSecondaryWebsite = async (accountId = "", website = "") => {
    const config = {
      url: `${URLS.addSecondaryWebsite}/${accountId}/secondary-website`,
      method: "POST",
      data: { website }
    };
    return await makeApi(config);
  };

  @action
  getAccountStages = async () => {
    const config = {
      url: URLS.getAccountStages,
      method: "GET"
    };
    return await makeApi(config);
  };

  @action
  getAccountStagesAnalytics = async (data = {}) => {
    const config = {
      url: URLS.getAccountAnalytics,
      method: "POST",
      data
    };
    setAccountsNetworkRequest("accountsFilter");
    const cancel = (value = null) =>
      cancelAccountsReqCallback("accountsFilter", value);
    return await makeApi(config, cancel);
  };

  @action
  getAccountsList = async (data = {}) => {
    const config = {
      url: URLS.getAccountList,
      method: "POST",
      data
    };
    setAccountsNetworkRequest("accounts");
    const cancel = (value = null) =>
      cancelAccountsReqCallback("accounts", value);
    return await makeApi(config, cancel);
  };

  // Contacts
  @action
  getAccountContactsStages = async () => {
    const config = {
      url: URLS.getContactStage,
      method: "GET"
    };
    let { data = [] } = await makeApi(config);
    data?.length && this.setContactsStages(data);
    return data;
  };

  @action
  getAccountContactsFilterAnalytics = async (data = {}) => {
    const config = {
      url: URLS.getContactFilter,
      method: "POST",
      data
    };
    setAccountsNetworkRequest("accountContactsFilter");
    const cancel = (value = null) =>
      cancelAccountsReqCallback("accountContactsFilter", value);
    return await makeApi(config, cancel);
  };

  @action
  getAccountContactsList = async (data = {}) => {
    const config = {
      url: URLS.getAllContacts,
      method: "POST",
      data
    };
    setAccountsNetworkRequest("accountContacts");
    const cancel = (value = null) =>
      cancelAccountsReqCallback("accountContacts", value);
    return await makeApi(config, cancel);
  };

  // Tasks
  @action
  getAccountTasksFilterAnalytics = async (data = {}) => {
    const config = {
      url: URLS.getAccountTasksAnalytics,
      method: "POST",
      data
    };
    setAccountsNetworkRequest("accountTasksFilter");
    const cancel = (value = null) =>
      cancelAccountsReqCallback("accountTasksFilter", value);
    return await makeApi(config, cancel);
  };

  @action
  getAccountTasksList = async (data = {}) => {
    const config = {
      url: URLS.getTaskFilter,
      method: "POST",
      data
    };
    setAccountsNetworkRequest("accountTasks");
    const cancel = (value = null) =>
      cancelAccountsReqCallback("accountTasks", value);
    return await makeApi(config, cancel);
  };
}

const accountsState = new AccountsState();
export { accountsState };
